
import React, { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { useToast } from '@/hooks/use-toast';
import { ThreadReply } from './types';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Loader2 } from 'lucide-react';

interface ThreadRepliesProps {
  threadId: string;
}

const ThreadReplies: React.FC<ThreadRepliesProps> = ({ threadId }) => {
  const [replies, setReplies] = useState<ThreadReply[]>([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  const fetchReplies = async () => {
    try {
      setLoading(true);
      
      const { data, error } = await supabase
        .from("club_thread_replies")
        .select(`
          *,
          profiles:user_id (id, full_name, username, avatar_url)
        `)
        .eq("thread_id", threadId)
        .order("created_at", { ascending: true });
        
      if (error) throw error;
      
      if (data) {
        const processedReplies = data.map(reply => ({
          ...reply,
          author: reply.profiles
        }));
        
        setReplies(processedReplies);
      }
    } catch (error) {
      console.error("Error fetching replies:", error);
      toast({
        title: "Error",
        description: "Failed to load replies",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReplies();
    
    // Set up realtime subscription
    const channel = supabase
      .channel('public:club_thread_replies')
      .on('postgres_changes', { 
        event: '*', 
        schema: 'public', 
        table: 'club_thread_replies',
        filter: `thread_id=eq.${threadId}` 
      }, () => {
        fetchReplies();
      })
      .subscribe();
    
    return () => {
      supabase.removeChannel(channel);
    };
  }, [threadId]);

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (replies.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        <p>No replies yet. Be the first to reply!</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-medium">Replies ({replies.length})</h3>
      
      {replies.map((reply) => (
        <Card key={reply.id} className="border-l-4 border-l-primary/20">
          <CardHeader className="py-3 flex flex-row items-center gap-2">
            <Avatar className="h-6 w-6">
              <AvatarImage src={reply.author?.avatar_url || ""} alt="Author" />
              <AvatarFallback>
                {reply.author?.full_name?.charAt(0) || reply.author?.username?.charAt(0) || "?"}
              </AvatarFallback>
            </Avatar>
            <div className="text-sm">
              <span className="font-medium">
                {reply.author?.full_name || reply.author?.username || "Anonymous"}
              </span>
              {" • "}
              <span className="text-xs text-muted-foreground">
                {new Date(reply.created_at).toLocaleDateString()} at {new Date(reply.created_at).toLocaleTimeString()}
              </span>
            </div>
          </CardHeader>
          <Separator />
          <CardContent className="pt-3">
            <p className="text-sm whitespace-pre-wrap">{reply.content}</p>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

export default ThreadReplies;
